import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Image as ImageIcon, Plus, Trash2, Edit2, Save, X, Search, FileImage, FileText, Layers, Check, AlertTriangle, Beaker } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = 'px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-200 w-full bg-white';

/**
 * InstructionsLibraryEditor — Settings → "Image library".
 *
 * Lets the admin upload photo-instructions and target them at one or many
 * (vehicle_type / manufacturer / model / engine / ecu_type / option) combinations
 * by picking from cascading dropdowns wired to the live price database. Empty
 * lists act as wildcards. The "Add another vehicle" UI lets one upload serve
 * many vehicles that share the same ECU/wiring at once.
 */
export default function InstructionsLibraryEditor({ token }) {
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('cards');
  const [items, setItems] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ option_slug: '', vehicle_type: '', manufacturer: '', model: '', ecu_type: '' });
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [optionSlugs, setOptionSlugs] = useState([]);
  const [vehicleStats, setVehicleStats] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [reconcileOrphan, setReconcileOrphan] = useState(null);
  const [testMatchId, setTestMatchId] = useState(null);

  const fetchJson = useCallback(async (path) => {
    const r = await fetch(`${API_URL}${path}`, { headers });
    return r.ok ? r.json() : null;
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => { if (v) p.append(k, v); });
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/instructions?${p.toString()}`, { headers }),
        fetch(`${API_URL}/api/instructions/entries?${p.toString()}`, { headers }),
      ]);
      const j1 = r1.ok ? await r1.json() : { items: [] };
      const j2 = r2.ok ? await r2.json() : { items: [] };
      setItems(j1.items || []);
      setEntries(j2.items || []);
    } finally { setLoading(false); }
  }, [filter, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOptionSlugs = useCallback(async () => {
    const j = await fetchJson('/api/instructions/option-slugs');
    setOptionSlugs(j?.items || []);
  }, [fetchJson]);

  const loadVehicleStats = useCallback(async () => {
    const j = await fetchJson('/api/instructions/vehicle-stats');
    setVehicleStats(j);
  }, [fetchJson]);

  const loadOrphans = useCallback(async () => {
    const j = await fetchJson('/api/instructions/orphans');
    setOrphans(j?.items || []);
  }, [fetchJson]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOptionSlugs(); loadVehicleStats(); loadOrphans(); }, [loadOptionSlugs, loadVehicleStats, loadOrphans]);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this instruction?')) return;
    const r = await fetch(`${API_URL}/api/instructions/${id}`, { method: 'DELETE', headers });
    if (r.ok) load();
  };

  const editing = editId ? items.find((i) => i.id === editId) : null;

  return (
    <div className="space-y-4" data-testid="instructions-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Photo Instruction Library</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Upload images that show up next to a tuning option in the order wizard.
            Empty filters in a row mean "all" — narrow it down only when needed.
          </p>
        </div>
        <button
          onClick={() => { setEditId(null); setShowForm(true); }}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-bold rounded-lg flex items-center gap-2 shadow"
          data-testid="instructions-add-btn"
        >
          <Plus className="w-4 h-4" /> Add instruction
        </button>
      </div>

      {/* Catalogue stats — mirrors Step 1 of the New Order wizard so
          the admin instantly sees how big the price DB the library
          targets is. Counts come from /api/instructions/vehicle-stats. */}
      {vehicleStats && (
        <div className="bg-gradient-to-r from-sky-50 via-white to-cyan-50 border border-sky-200 rounded-xl px-4 py-3" data-testid="library-stats">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm">
              <span className="text-slate-500">Price DB:</span>{' '}
              <span className="font-bold text-slate-800 tabular-nums">{vehicleStats.total.toLocaleString()}</span>{' '}
              <span className="text-slate-500">vehicles ·</span>{' '}
              <span className="font-bold text-slate-800 tabular-nums">{optionSlugs.length}</span>{' '}
              <span className="text-slate-500">tuning options available</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(vehicleStats.by_vehicle_type || []).map((v) => (
                <span key={v.name}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-sky-200 text-[11px]"
                  data-testid={`vehicle-stat-${v.name}`}
                >
                  <span className="text-slate-700">{v.name}</span>
                  <span className="font-bold text-cyan-700 tabular-nums">{v.count.toLocaleString()}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Orphan slugs banner — surfaces Photo Library rows whose
          `option_slug` is no longer present in the live tuning-options
          catalogue (typically because the admin renamed the slug).
          Reconcile re-maps them to a current slug via
          POST /api/instructions/rename-slug. */}
      {orphans.length > 0 && (
        <div
          className="border-2 border-amber-300 bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-3"
          data-testid="instructions-orphan-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-amber-900 text-sm">
              {orphans.length === 1
                ? '1 orphaned tuning-option slug detected'
                : `${orphans.length} orphaned tuning-option slugs detected`}
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              These slugs exist in the Photo Library but are no longer in the live tuning-options catalogue
              (the option was probably renamed). Re-map each one to a current slug so the photos keep matching orders.
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {orphans.map((o) => (
                <button
                  key={o.old_slug}
                  onClick={() => setReconcileOrphan(o)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-amber-300 text-xs hover:bg-amber-100"
                  data-testid={`instructions-orphan-${o.old_slug}`}
                >
                  <code className="font-mono text-amber-900">{o.old_slug}</code>
                  <span className="text-amber-700 font-bold tabular-nums">×{o.instruction_count}</span>
                  <span className="text-amber-600">→ reconcile</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter strip — also wired through the price DB cascade. */}
      <FilterBar
        token={token} filter={filter} setFilter={setFilter}
        optionSlugs={optionSlugs}
      />

      {/* View switcher */}
      <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="instructions-tabs">
        <button
          onClick={() => setTab('cards')}
          className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${tab === 'cards' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
          data-testid="instructions-tab-cards"
        >
          Cards <span className="opacity-60">({items.length})</span>
        </button>
        <button
          onClick={() => setTab('entries')}
          className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${tab === 'entries' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
          data-testid="instructions-tab-entries"
        >
          Entries <span className="opacity-60">({entries.length})</span>
        </button>
        <button
          onClick={() => setTab('quick')}
          className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${tab === 'quick' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
          data-testid="instructions-tab-quick"
        >
          Quick add (by ECU)
        </button>
      </div>

      {/* Card grid */}
      {tab === 'cards' && (loading ? (
        <div className="py-10 text-center text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-sm" data-testid="instructions-empty">
          <FileImage className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          No instructions yet. Click <b>Add instruction</b> to upload your first image.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <Card
              key={it.id}
              item={it}
              onEdit={() => { setEditId(it.id); setShowForm(true); }}
              onDelete={() => onDelete(it.id)}
              onTest={() => setTestMatchId(it.id)}
            />
          ))}
        </div>
      ))}

      {tab === 'entries' && (
        <EntriesTable
          token={token}
          entries={entries}
          loading={loading}
          onChanged={load}
        />
      )}

      {tab === 'quick' && (
        <QuickAddBySearch token={token} optionSlugs={optionSlugs} onSaved={load} />
      )}

      {showForm && (
        <InstructionForm
          token={token}
          existing={editing}
          optionSlugs={optionSlugs}
          onClose={() => { setShowForm(false); setEditId(null); }}
          onSaved={() => { setShowForm(false); setEditId(null); load(); loadOptionSlugs(); }}
        />
      )}

      {reconcileOrphan && (
        <ReconcileSlugModal
          token={token}
          orphan={reconcileOrphan}
          optionSlugs={optionSlugs}
          onClose={() => setReconcileOrphan(null)}
          onDone={() => { setReconcileOrphan(null); load(); loadOrphans(); }}
        />
      )}

      {testMatchId && (
        <TestMatchModal
          token={token}
          instruction={items.find((it) => it.id === testMatchId)}
          onClose={() => setTestMatchId(null)}
        />
      )}
    </div>
  );
}


/** Top filter strip — same vehicle_type → manufacturer → model → ECU cascade as New Order. */
function FilterBar({ token, filter, setFilter, optionSlugs }) {
  const cascade = useCascade(token, filter.vehicle_type, filter.manufacturer, filter.model);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="instructions-filter">
      <select
        className={inputCls} value={filter.option_slug}
        onChange={(e) => setFilter({ ...filter, option_slug: e.target.value })}
        data-testid="instructions-filter-option"
      >
        <option value="">All options</option>
        {optionSlugs.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
      </select>
      <select
        className={inputCls} value={filter.vehicle_type}
        onChange={(e) => setFilter({ ...filter, vehicle_type: e.target.value, manufacturer: '', model: '', ecu_type: '' })}
        data-testid="instructions-filter-vehicle-type"
      >
        <option value="">All vehicle types</option>
        {cascade.vehicleTypes.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <select
        className={inputCls} value={filter.manufacturer}
        onChange={(e) => setFilter({ ...filter, manufacturer: e.target.value, model: '', ecu_type: '' })}
        disabled={!filter.vehicle_type}
        data-testid="instructions-filter-manufacturer"
      >
        <option value="">All manufacturers</option>
        {cascade.brands.map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
      <select
        className={inputCls} value={filter.model}
        onChange={(e) => setFilter({ ...filter, model: e.target.value, ecu_type: '' })}
        disabled={!filter.manufacturer}
        data-testid="instructions-filter-model"
      >
        <option value="">All models</option>
        {cascade.models.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select
        className={inputCls} value={filter.ecu_type}
        onChange={(e) => setFilter({ ...filter, ecu_type: e.target.value })}
        disabled={!filter.model}
        data-testid="instructions-filter-ecu"
      >
        <option value="">All ECU types</option>
        {cascade.ecus.map((e) => <option key={e} value={e}>{e}</option>)}
      </select>
    </div>
  );
}


/** Re-usable cascade hook — shared by the filter bar and every row in the
 *  form. Reads the SAME endpoints as the customer order wizard
 *  (/api/price-catalog/*), i.e. the live Excel catalog. It used to read the
 *  legacy /api/pricing/* endpoints, which are backed by the old
 *  `vehicle_prices` collection — that collection is no longer refreshed by
 *  the Excel import, so thousands of newer brands/models/ECUs were missing
 *  from the library's dropdowns while the order wizard had them. */
const cascadeCache = new Map();
function cachedList(url, token, pick) {
  if (!cascadeCache.has(url)) {
    cascadeCache.set(url, fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(j => (Array.isArray(j) ? j : pick(j)))
      .catch(() => { cascadeCache.delete(url); return []; }));
  }
  return cascadeCache.get(url);
}

function useCascade(token, vehicleType, manufacturer, model) {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [ecus, setEcus] = useState([]);
  const vt = encodeURIComponent(vehicleType || '');
  const br = encodeURIComponent(manufacturer || '');

  useEffect(() => {
    cachedList(`${API_URL}/api/price-catalog/vehicle-types`, token, j => j?.types || []).then(setVehicleTypes);
  }, [token]);

  useEffect(() => {
    if (!vehicleType) { setBrands([]); return; }
    cachedList(`${API_URL}/api/price-catalog/brands?vehicle_type=${vt}`, token, j => j?.brands || []).then(setBrands);
  }, [vehicleType, vt, token]);

  useEffect(() => {
    if (!manufacturer) { setModels([]); return; }
    cachedList(`${API_URL}/api/price-catalog/models?vehicle_type=${vt}&brand=${br}`, token, j => j?.models || []).then(setModels);
  }, [manufacturer, vt, br, token]);

  // ECUs for the model, or for the whole brand when "Any model".
  useEffect(() => {
    if (!manufacturer) { setEcus([]); return; }
    const base = `vehicle_type=${vt}&brand=${br}`;
    const url = model
      ? `${API_URL}/api/price-catalog/ecu-types?${base}&model=${encodeURIComponent(model)}`
      : `${API_URL}/api/price-catalog/ecu-types-by-brand?${base}`;
    cachedList(url, token, j => j?.ecus || j?.ecu_types || []).then(setEcus);
  }, [model, manufacturer, vt, br, token]);

  return { vehicleTypes, brands, models, ecus };
}


/** One instruction card in the grid view. */
function Card({ item, onEdit, onDelete, onTest }) {
  const summary = (label, arr) => arr && arr.length > 0
    ? <span><b className="text-slate-600">{label}:</b> {arr.slice(0, 4).join(', ')}{arr.length > 4 ? `… (+${arr.length - 4})` : ''}</span>
    : <span className="text-slate-400">{label}: any</span>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition" data-testid={`instructions-card-${item.id}`}>
      <div className="aspect-[16/10] bg-slate-100 relative">
        <Thumb url={item.image_url} fit="object-contain" />
        <span className="absolute top-2 left-2 px-2 py-0.5 bg-cyan-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
          {item.option_slug}
        </span>
        {item.pdf_url && (
          <a
            href={`${API_URL}${item.pdf_url}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={item.pdf_original_name || 'PDF manual'}
            className="absolute top-2 right-2 px-2 py-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full uppercase tracking-wider flex items-center gap-1 hover:bg-rose-700"
            data-testid={`instructions-card-pdf-${item.id}`}
          >
            <FileText className="w-3 h-3" /> PDF
          </a>
        )}
      </div>
      <div className="p-3 space-y-1 text-xs">
        <div>{summary('Vehicle', item.vehicle_types)}</div>
        <div>{summary('Brand',   item.manufacturers)}</div>
        <div>{summary('Model',   item.models)}</div>
        <div>{summary('Engine',  item.engines)}</div>
        <div>{summary('ECU',     item.ecu_types)}</div>
        {item.text_override && <div className="mt-1 italic text-slate-500 line-clamp-2">"{item.text_override}"</div>}
      </div>
      <div className="border-t border-slate-100 px-3 py-2 flex justify-end gap-1">
        {onTest && (
          <button
            onClick={onTest}
            className="p-1.5 text-slate-500 hover:text-emerald-600"
            title="Test match against current price catalog"
            data-testid={`instructions-test-${item.id}`}
          >
            <Beaker className="w-4 h-4" />
          </button>
        )}
        <button onClick={onEdit} className="p-1.5 text-slate-500 hover:text-blue-600" data-testid={`instructions-edit-${item.id}`}><Edit2 className="w-4 h-4" /></button>
        <button onClick={onDelete} className="p-1.5 text-slate-500 hover:text-rose-600" data-testid={`instructions-delete-${item.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}


/** Modal form — create or edit. Uses cascading rows so the admin can attach
 *  one image to multiple (vehicle_type / brand / model / engine / ecu) combos. */
function InstructionForm({ token, existing, optionSlugs, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const isEdit = !!existing;

  const [optionSlug, setOptionSlug]       = useState(existing?.option_slug || '');
  const [textOverride, setTextOverride]   = useState(existing?.text_override || '');
  const [notes, setNotes]                 = useState(existing?.notes || '');
  const [image, setImage]                 = useState(null);
  const [imagePreview, setImagePreview]   = useState(existing?.image_url ? `${API_URL}${existing.image_url}` : null);
  const [pdfFile, setPdfFile]             = useState(null);
  const [existingPdf, setExistingPdf]     = useState(
    existing?.pdf_url ? { url: existing.pdf_url, name: existing.pdf_original_name || 'instruction.pdf' } : null,
  );

  // Targeting rows — each row picks one (vehicle_type, brand, model, engine, ecu).
  // On save we union them into the 5 string-array columns the backend expects.
  // When loading an existing instruction we expand each non-empty array into
  // separate rows so the admin can edit them one at a time.
  const initialRows = useMemo(() => {
    if (!existing) return [{ id: crypto.randomUUID(), vehicle_type: '', manufacturer: '', model: '', engine: '', ecu_type: '' }];
    const maxLen = Math.max(
      (existing.vehicle_types || []).length, (existing.manufacturers || []).length,
      (existing.models || []).length, (existing.engines || []).length,
      (existing.ecu_types || []).length, 1,
    );
    return Array.from({ length: maxLen }, (_, i) => ({
      id: crypto.randomUUID(),
      vehicle_type: (existing.vehicle_types || [])[i] || '',
      manufacturer: (existing.manufacturers || [])[i] || '',
      model:        (existing.models || [])[i] || '',
      engine:       (existing.engines || [])[i] || '',
      ecu_type:     (existing.ecu_types || [])[i] || '',
    }));
  }, [existing]);
  const [rows, setRows] = useState(initialRows);
  const [saving, setSaving] = useState(false);
  // "Find similar" UI state — populated when admin clicks the button on a row.
  const [similar, setSimilar] = useState({ open: false, items: [], ecu: '', sourceIdx: -1, search: '' });
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarSelected, setSimilarSelected] = useState(new Set());

  const updateRow = (idx, patch) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const addRow    = () => setRows(rs => [...rs, { id: crypto.randomUUID(), vehicle_type: '', manufacturer: '', model: '', engine: '', ecu_type: '' }]);
  const removeRow = (idx) => setRows(rs => rs.length === 1 ? rs : rs.filter((_, i) => i !== idx));
  const [expanding, setExpanding] = useState(-1);

  /** Brand + ECU picked with "Any model" → replace that row with every
   *  catalog vehicle of that brand using the ECU. */
  const expandByEcu = async (idx, ecu) => {
    const src = rows[idx];
    setExpanding(idx);
    try {
      const p = new URLSearchParams({ ecu_type: ecu, vehicle_type: src.vehicle_type, brand: src.manufacturer });
      const r = await fetch(`${API_URL}/api/instructions/similar-vehicles?${p.toString()}`, { headers });
      const items = r.ok ? ((await r.json()).items || []) : [];
      if (items.length === 0) { updateRow(idx, { ecu_type: ecu }); return; }
      setRows(rs => {
        const seen = new Set(rs.filter((_, i) => i !== idx).map(x => `${x.vehicle_type}|${x.manufacturer}|${x.model}|${x.engine}|${x.ecu_type}`));
        const add = items.filter(v => !seen.has(similarKey(v))).map(v => ({
          id: crypto.randomUUID(), vehicle_type: v.vehicle_type, manufacturer: v.brand,
          model: v.model, engine: v.engine, ecu_type: v.ecu_type,
        }));
        return [...rs.slice(0, idx), ...add, ...rs.slice(idx + 1)];
      });
    } finally { setExpanding(-1); }
  };

  /** "Find similar" — load every other vehicle that uses the same ECU as the
   *  given source row and present them as a checkbox list. */
  const openSimilar = async (idx) => {
    const src = rows[idx];
    if (!src.ecu_type) {
      alert('Pick an ECU type on this row first — that\'s how I find similar vehicles.');
      return;
    }
    setSimilar({ open: true, items: [], ecu: src.ecu_type, sourceIdx: idx, search: '' });
    setSimilarSelected(new Set());
    setSimilarLoading(true);
    try {
      const p = new URLSearchParams({ ecu_type: src.ecu_type });
      if (src.vehicle_type)  p.append('vehicle_type',  src.vehicle_type);
      if (src.manufacturer) { p.append('exclude_brand', src.manufacturer); }
      if (src.model)        { p.append('exclude_model', src.model); }
      const r = await fetch(`${API_URL}/api/instructions/similar-vehicles?${p.toString()}`, { headers });
      const j = r.ok ? await r.json() : { items: [] };
      setSimilar(s => ({ ...s, items: j.items || [] }));
    } finally { setSimilarLoading(false); }
  };

  const toggleSimilarKey = (key) => {
    setSimilarSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const applySimilar = () => {
    // Materialise each selected vehicle as a new targeting row.  Skip combos
    // that already exist (someone might re-open the dialog after applying).
    const existing = new Set(rows.map(r => `${r.vehicle_type}|${r.manufacturer}|${r.model}|${r.engine}|${r.ecu_type}`));
    const additions = [];
    for (const key of similarSelected) {
      const item = similar.items.find(x => similarKey(x) === key);
      if (!item) continue;
      const fp = `${item.vehicle_type}|${item.brand}|${item.model}|${item.engine}|${item.ecu_type}`;
      if (existing.has(fp)) continue;
      existing.add(fp);
      additions.push({
        id: crypto.randomUUID(),
        vehicle_type: item.vehicle_type, manufacturer: item.brand,
        model: item.model, engine: item.engine, ecu_type: item.ecu_type,
      });
    }
    if (additions.length === 0) {
      setSimilar({ open: false, items: [], ecu: '', sourceIdx: -1, search: '' });
      return;
    }
    setRows(rs => {
      // If the very first row is empty/blank, replace it; otherwise just append.
      const empty = (r) => !r.vehicle_type && !r.manufacturer && !r.model && !r.engine && !r.ecu_type;
      if (rs.length === 1 && empty(rs[0])) return additions;
      return [...rs, ...additions];
    });
    setSimilar({ open: false, items: [], ecu: '', sourceIdx: -1, search: '' });
    setSimilarSelected(new Set());
  };

  const filteredSimilar = useMemo(() => {
    const s = (similar.search || '').toLowerCase();
    if (!s) return similar.items;
    return similar.items.filter(x =>
      `${x.brand} ${x.model} ${x.engine} ${x.vehicle_type}`.toLowerCase().includes(s)
    );
  }, [similar.items, similar.search]);

  // Group the similar vehicles by brand for a tidy UI.
  const groupedSimilar = useMemo(() => {
    const map = new Map();
    filteredSimilar.forEach(v => {
      const arr = map.get(v.brand) || [];
      arr.push(v);
      map.set(v.brand, arr);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredSimilar]);

  const allFilteredSelected = filteredSimilar.length > 0 &&
    filteredSimilar.every(v => similarSelected.has(similarKey(v)));
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      setSimilarSelected(prev => {
        const next = new Set(prev);
        filteredSimilar.forEach(v => next.delete(similarKey(v)));
        return next;
      });
    } else {
      setSimilarSelected(prev => {
        const next = new Set(prev);
        filteredSimilar.forEach(v => next.add(similarKey(v)));
        return next;
      });
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImage(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const dropExistingPdf = async () => {
    if (!existing?.id) { setExistingPdf(null); return; }
    if (!window.confirm('Remove the PDF manual from this instruction?')) return;
    const r = await fetch(`${API_URL}/api/instructions/${existing.id}/pdf`, { method: 'DELETE', headers });
    if (r.ok) setExistingPdf(null);
    else alert((await r.json().catch(() => ({}))).detail || 'Could not remove the PDF');
  };

  const onSave = async () => {
    if (!optionSlug.trim()) { alert('Pick a tuning option first.'); return; }
    if (!isEdit && !image && !pdfFile) { alert('Attach an image or a PDF.'); return; }
    setSaving(true);
    try {
      // Union the row values into the 5 array columns.
      const collect = (k) => Array.from(new Set(rows.map(r => (r[k] || '').trim()).filter(Boolean)));
      if (isEdit) {
        const r = await fetch(`${API_URL}/api/instructions/${existing.id}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            option_slug:   optionSlug,
            vehicle_types: collect('vehicle_type'),
            manufacturers: collect('manufacturer'),
            models:        collect('model'),
            engines:       collect('engine'),
            ecu_types:     collect('ecu_type'),
            text_override: textOverride,
            notes,
          }),
        });
        if (!r.ok) { alert('Save failed: ' + ((await r.json().catch(() => ({}))).detail || r.status)); return; }
        if (pdfFile) {
          const pfd = new FormData();
          pfd.append('pdf', pdfFile);
          const pr = await fetch(`${API_URL}/api/instructions/${existing.id}/pdf`, { method: 'POST', headers, body: pfd });
          if (!pr.ok) { alert('PDF upload failed: ' + ((await pr.json().catch(() => ({}))).detail || pr.status)); return; }
        }
      } else {
        const fd = new FormData();
        fd.append('option_slug',   optionSlug);
        fd.append('vehicle_types', collect('vehicle_type').join(','));
        fd.append('manufacturers', collect('manufacturer').join(','));
        fd.append('models',        collect('model').join(','));
        fd.append('engines',       collect('engine').join(','));
        fd.append('ecu_types',     collect('ecu_type').join(','));
        fd.append('text_override', textOverride);
        fd.append('notes', notes);
        if (image) fd.append('image', image);
        if (pdfFile) fd.append('pdf', pdfFile);
        const r = await fetch(`${API_URL}/api/instructions`, { method: 'POST', headers, body: fd });
        if (!r.ok) { alert('Save failed: ' + ((await r.json().catch(() => ({}))).detail || r.status)); return; }
      }
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="instructions-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative">
        {/* "Find similar" panel — overlays the right edge as a slide-in drawer */}
        {similar.open && (
          <SimilarPanel
            ecu={similar.ecu}
            search={similar.search}
            onSearch={(v) => setSimilar(s => ({ ...s, search: v }))}
            grouped={groupedSimilar}
            count={filteredSimilar.length}
            total={similar.items.length}
            loading={similarLoading}
            selected={similarSelected}
            toggle={toggleSimilarKey}
            allSelected={allFilteredSelected}
            toggleAll={toggleAllFiltered}
            onApply={applySimilar}
            onClose={() => setSimilar({ open: false, items: [], ecu: '', sourceIdx: -1, search: '' })}
          />
        )}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">{isEdit ? 'Edit instruction' : 'Add new instruction'}</h3>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Option + image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Tuning option *</Label>
              <select className={inputCls} value={optionSlug} onChange={(e) => setOptionSlug(e.target.value)} data-testid="instructions-form-option">
                <option value="">— select —</option>
                {optionSlugs.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">List comes from your price database (tuning options).</p>
            </div>
            <div>
              <Label>Image {isEdit ? '(leave blank to keep current)' : <span className="font-normal text-slate-400">(image or PDF required)</span>}</Label>
              <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" onChange={onPick} className={inputCls} data-testid="instructions-form-image" />
              {imagePreview && (
                <div className="mt-2 aspect-[16/10] bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                  <img src={imagePreview} alt="" className="w-full h-full object-contain" />
                </div>
              )}
              <Label className="mt-3">PDF manual</Label>
              <input
                type="file" accept="application/pdf,.pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className={inputCls}
                data-testid="instructions-form-pdf"
              />
              {pdfFile ? (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-rose-700">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{pdfFile.name}</span>
                  <button type="button" onClick={() => setPdfFile(null)} className="text-slate-400 hover:text-rose-600 underline ml-1">remove</button>
                </div>
              ) : existingPdf ? (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600">
                  <FileText className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
                  <a href={`${API_URL}${existingPdf.url}`} target="_blank" rel="noreferrer" className="truncate underline hover:text-cyan-700">{existingPdf.name}</a>
                  <button type="button" onClick={dropExistingPdf} className="text-slate-400 hover:text-rose-600 underline ml-1" data-testid="instructions-form-pdf-remove">delete</button>
                </div>
              ) : null}
              <div className="text-[11px] text-slate-400 mt-1">Customers open it on the order page, page by page.</div>
            </div>
          </div>

          {/* Targeting rows */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="!mb-0">Apply to vehicles <span className="text-slate-400 font-normal text-xs">(empty fields = "any")</span></Label>
              <button
                type="button" onClick={addRow}
                className="px-2.5 py-1 text-xs font-bold bg-cyan-50 text-cyan-700 border border-cyan-200 rounded flex items-center gap-1 hover:bg-cyan-100"
                data-testid="instructions-form-add-row"
              >
                <Layers className="w-3 h-3" /> Add another vehicle
              </button>
            </div>
            <div className="space-y-2">
              {rows.map((row, idx) => (
                <RowSelector
                  key={row.id} row={row} idx={idx} token={token}
                  onChange={(patch) => updateRow(idx, patch)}
                  onRemove={() => removeRow(idx)}
                  canRemove={rows.length > 1}
                  onFindSimilar={() => openSimilar(idx)}
                  onPickEcuAnyModel={(ecu) => expandByEcu(idx, ecu)}
                  expanding={expanding === idx}
                />
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Tip: pick a brand, keep “Any model” and choose a block in the ECU column — every model of that brand with this block is added at once.
            </p>
          </div>

          {/* Optional overrides */}
          <div>
            <Label>Optional text override</Label>
            <textarea rows={2} className={inputCls} value={textOverride} onChange={(e) => setTextOverride(e.target.value)} placeholder="Leave blank to keep the original price-DB hint" data-testid="instructions-form-override" />
          </div>
          <div>
            <Label>Admin notes (never shown to customers)</Label>
            <input type="text" className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="instructions-form-notes" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
          <button
            onClick={onSave} disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            data-testid="instructions-form-save"
          >
            {saving ? <><Save className="w-4 h-4 animate-pulse" /> Saving…</> : <><Check className="w-4 h-4" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}


/** Instruction thumbnail; PDF-only instructions show a PDF placeholder. */
function Thumb({ url, fit = 'object-cover' }) {
  if (!url) return <div className="w-full h-full flex items-center justify-center text-rose-600 bg-rose-50"><FileText className="w-1/3 h-1/3 max-w-12 max-h-12" /></div>;
  return <img src={`${API_URL}${url}`} alt="" className={`w-full h-full ${fit}`} loading="lazy" />;
}

/** Stable key for a similar-vehicle row. */
function similarKey(v) {
  return `${v.vehicle_type}|${v.brand}|${v.model}|${v.engine}|${v.ecu_type}`;
}


/** One targeting row — five cascaded selectors. Each row is independent so
 *  picking a different brand on row 2 doesn't reset row 1. */
function RowSelector({ row, idx, token, onChange, onRemove, canRemove, onFindSimilar, onPickEcuAnyModel, expanding }) {
  const cascade = useCascade(token, row.vehicle_type, row.manufacturer, row.model);
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto_auto] gap-1.5 p-2 bg-slate-50 rounded-lg" data-testid={`instructions-form-row-${idx}`}>
      <select className={inputCls} value={row.vehicle_type} onChange={(e) => onChange({ vehicle_type: e.target.value, manufacturer: '', model: '', engine: '', ecu_type: '' })} data-testid={`instructions-row-${idx}-vehicle`}>
        <option value="">Any vehicle</option>
        {cascade.vehicleTypes.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <select className={inputCls} value={row.manufacturer} onChange={(e) => onChange({ manufacturer: e.target.value, model: '', engine: '', ecu_type: '' })} disabled={!row.vehicle_type} data-testid={`instructions-row-${idx}-brand`}>
        <option value="">Any brand</option>
        {cascade.brands.map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
      <select className={inputCls} value={row.model} onChange={(e) => onChange({ model: e.target.value, engine: '', ecu_type: '' })} disabled={!row.manufacturer} data-testid={`instructions-row-${idx}-model`}>
        <option value="">Any model</option>
        {cascade.models.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <input
        type="text" className={inputCls} value={row.engine}
        onChange={(e) => onChange({ engine: e.target.value })}
        placeholder="Any engine"
        data-testid={`instructions-row-${idx}-engine`}
      />
      <select
        className={inputCls} value={row.ecu_type} disabled={expanding}
        onChange={(e) => {
          const v = e.target.value;
          if (v && row.manufacturer && !row.model) onPickEcuAnyModel(v);
          else onChange({ ecu_type: v });
        }}
        title={row.manufacturer && !row.model ? 'Pick a block to add every model of this brand that uses it' : undefined}
        data-testid={`instructions-row-${idx}-ecu`}
      >
        <option value="">{expanding ? 'Loading…' : (row.manufacturer && !row.model ? 'By ECU (all models)' : 'Any ECU')}</option>
        {cascade.ecus.map((e) => <option key={e} value={e}>{e}</option>)}
      </select>
      <button
        onClick={() => onFindSimilar()}
        disabled={!row.ecu_type}
        title={row.ecu_type ? `Find every vehicle in the price DB that uses ${row.ecu_type}` : 'Pick an ECU type to enable this'}
        className="p-2 text-cyan-600 hover:bg-cyan-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={`instructions-row-${idx}-find-similar`}
      >
        <Search className="w-4 h-4" />
      </button>
      <button
        onClick={onRemove} disabled={!canRemove}
        className="p-2 text-rose-500 hover:bg-rose-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
        title="Remove this row"
        data-testid={`instructions-row-${idx}-remove`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}


function Label({ children, className = '' }) {
  return <div className={`text-xs font-bold text-slate-600 mb-1 ${className}`}>{children}</div>;
}


/**
 * QuickAddBySearch — free-text fast batch attachment.
 *
 *  1. Admin picks Vehicle type → (optional) brand → the ECU seen on the car,
 *     exactly like the customer's "Find your model by ECU" helper in the
 *     order wizard. Every catalog vehicle using that ECU is listed at once.
 *  2. Matching vehicles render grouped by brand. Filter / select all per
 *     brand / per-row.
 *  3. Pick a tuning option, upload a photo (+ optional PDF manual), save →
 *     one instruction with N targeting tuples.
 */
function QuickAddBySearch({ token, optionSlugs, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [vehicleType, setVehicleType] = useState('');
  const [brand, setBrand] = useState('');
  const [ecuType, setEcuType] = useState('');
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [ecus, setEcus] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(new Set()); // similarKey()
  const [optionSlug, setOptionSlug] = useState('');
  const [textOverride, setTextOverride] = useState('');
  const [notes, setNotes] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState('');

  // Cascade sources — the live Excel catalog, same as the order wizard.
  useEffect(() => {
    (async () => {
      const r = await fetch(`${API_URL}/api/price-catalog/vehicle-types`, { headers });
      setVehicleTypes(r.ok ? await r.json() : []);
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBrand(''); setEcuType(''); setBrands([]);
    if (!vehicleType) return;
    (async () => {
      const r = await fetch(`${API_URL}/api/price-catalog/brands?vehicle_type=${encodeURIComponent(vehicleType)}`, { headers });
      setBrands(r.ok ? await r.json() : []);
    })();
  }, [vehicleType, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ECU list — for the whole vehicle type, or narrowed to one brand.
  useEffect(() => {
    setEcuType(''); setEcus([]);
    if (!vehicleType) return;
    (async () => {
      const p = new URLSearchParams({ vehicle_type: vehicleType });
      if (brand) p.append('brand', brand);
      const r = await fetch(`${API_URL}/api/price-catalog/ecu-types-by-brand?${p.toString()}`, { headers });
      setEcus(r.ok ? await r.json() : []);
    })();
  }, [vehicleType, brand, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every catalog vehicle that carries the chosen ECU.
  useEffect(() => {
    if (!ecuType) { setVehicles([]); setSelected(new Set()); setHint(''); return; }
    (async () => {
      setVehiclesLoading(true);
      try {
        const p = new URLSearchParams({ ecu_type: ecuType, vehicle_type: vehicleType });
        if (brand) p.append('brand', brand);
        const r = await fetch(`${API_URL}/api/instructions/similar-vehicles?${p.toString()}`, { headers });
        const j = r.ok ? await r.json() : { items: [] };
        const items = j.items || [];
        setVehicles(items);
        setHint(items.length >= 1000 ? 'Showing first 1000 vehicles — narrow by brand for the rest.' : '');
        setSelected(new Set(items.map(similarKey)));  // pre-select all — that's the point of bulk attach
      } finally { setVehiclesLoading(false); }
    })();
  }, [ecuType, vehicleType, brand, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const s = filter.toLowerCase();
    if (!s) return vehicles;
    return vehicles.filter(v => `${v.brand} ${v.model} ${v.engine} ${v.vehicle_type} ${v.ecu_type}`.toLowerCase().includes(s));
  }, [vehicles, filter]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(v => {
      const arr = map.get(v.brand) || [];
      arr.push(v); map.set(v.brand, arr);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const allOn = filtered.length > 0 && filtered.every(v => selected.has(similarKey(v)));
  const toggleAll = () => {
    if (allOn) setSelected(prev => { const n = new Set(prev); filtered.forEach(v => n.delete(similarKey(v))); return n; });
    else       setSelected(prev => { const n = new Set(prev); filtered.forEach(v => n.add(similarKey(v))); return n; });
  };
  const toggleBrand = (items) => {
    const allBrandOn = items.every(v => selected.has(similarKey(v)));
    setSelected(prev => {
      const n = new Set(prev);
      items.forEach(v => allBrandOn ? n.delete(similarKey(v)) : n.add(similarKey(v)));
      return n;
    });
  };
  const toggleOne = (key) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImage(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setVehicleType(''); setBrand(''); setEcuType('');
    setVehicles([]); setSelected(new Set());
    setOptionSlug(''); setTextOverride(''); setNotes('');
    setImage(null); setImagePreview(null); setPdfFile(null); setHint('');
  };

  const onSave = async () => {
    if (!optionSlug) { alert('Pick a tuning option first.'); return; }
    if (!image && !pdfFile) { alert('Attach an image or a PDF.'); return; }
    if (selected.size === 0) { alert('Select at least one vehicle.'); return; }
    setSaving(true);
    try {
      const picked = vehicles.filter(v => selected.has(similarKey(v)));
      const collect = (k) => Array.from(new Set(picked.map(v => v[k] || '').filter(Boolean)));
      const fd = new FormData();
      fd.append('option_slug',   optionSlug);
      fd.append('vehicle_types', collect('vehicle_type').join(','));
      fd.append('manufacturers', collect('brand').join(','));
      fd.append('models',        collect('model').join(','));
      fd.append('engines',       collect('engine').join(','));
      fd.append('ecu_types',     collect('ecu_type').join(','));
      fd.append('text_override', textOverride);
      fd.append('notes', notes);
      if (image) fd.append('image', image);
      if (pdfFile) fd.append('pdf', pdfFile);
      const r = await fetch(`${API_URL}/api/instructions`, { method: 'POST', headers, body: fd });
      if (!r.ok) {
        alert('Save failed: ' + ((await r.json().catch(() => ({}))).detail || r.status));
        return;
      }
      reset();
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid="instructions-quick-add">
      {/* Step 1 — pick the ECU you see on the car */}
      <section>
        <div className="flex items-baseline justify-between mb-2 gap-2">
          <Label className="!mb-0">1. Find vehicles by ECU</Label>
          {(vehicleType || vehicles.length > 0) && <button onClick={reset} className="text-xs text-slate-500 hover:text-rose-600 underline" data-testid="instructions-quick-reset">Clear</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Vehicle type</div>
            <select className={inputCls} value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} data-testid="instructions-quick-vehicle-type">
              <option value="">— select —</option>
              {vehicleTypes.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Manufacturer <span className="normal-case font-normal text-slate-400">(optional)</span></div>
            <select className={inputCls} value={brand} onChange={(e) => setBrand(e.target.value)} disabled={!vehicleType} data-testid="instructions-quick-brand">
              <option value="">All brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
              ECU on the car {ecus.length > 0 && <span className="normal-case font-normal text-cyan-700">· {ecus.length} available</span>}
            </div>
            <select className={inputCls} value={ecuType} onChange={(e) => setEcuType(e.target.value)} disabled={!vehicleType} data-testid="instructions-quick-ecu">
              <option value="">— select ECU —</option>
              {ecus.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          Pick the ECU and every vehicle in the price catalog that carries it is listed below, pre-selected — attach one photo to all of them in a single save.
        </div>
        {ecuType && !vehiclesLoading && (
          <div className="text-xs text-slate-500 mt-1">
            Found <b className="text-slate-800">{vehicles.length}</b> vehicle{vehicles.length === 1 ? '' : 's'} with <b className="text-slate-800">{ecuType}</b>.
            {hint && <span className="ml-2 text-amber-600">{hint}</span>}
          </div>
        )}
      </section>

      {/* Step 2 — vehicle picker */}
      {vehicles.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
            <Label className="!mb-0">2. Pick vehicles to attach the photo to</Label>
            <div className="flex items-center gap-2">
              <input
                type="text" placeholder="Filter results…"
                value={filter} onChange={(e) => setFilter(e.target.value)}
                className="px-2 py-1 text-xs border border-slate-300 rounded"
                data-testid="instructions-quick-filter"
              />
              <button onClick={toggleAll} disabled={!filtered.length}
                className="px-2 py-1 text-xs font-bold text-cyan-700 hover:bg-cyan-50 rounded disabled:opacity-50"
                data-testid="instructions-quick-toggle-all"
              >
                {allOn ? 'Deselect all' : 'Select all visible'}
              </button>
              <span className="text-xs text-slate-500"><b className="text-cyan-700">{selected.size}</b> selected</span>
            </div>
          </div>
          {vehiclesLoading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
          ) : grouped.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No vehicles match your filter.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
              {grouped.map(([brand, items]) => {
                const allBrandOn = items.every(v => selected.has(similarKey(v)));
                return (
                  <div key={brand} data-testid={`instructions-quick-group-${brand}`}>
                    <button
                      onClick={() => toggleBrand(items)}
                      className={`w-full px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider sticky top-0 z-1 flex items-center justify-between gap-2 ${allBrandOn ? 'bg-cyan-100 text-cyan-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`instructions-quick-brand-toggle-${brand}`}
                    >
                      <span>{brand} ({items.length})</span>
                      <span>{allBrandOn ? '✓ all' : 'pick all'}</span>
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-50">
                      {items.map((v) => {
                        const k = similarKey(v);
                        const on = selected.has(k);
                        return (
                          <label key={k} className={`flex items-start gap-2 px-3 py-1.5 cursor-pointer text-xs ${on ? 'bg-cyan-50' : 'bg-white hover:bg-slate-50'}`} data-testid={`instructions-quick-row-${k}`}>
                            <input type="checkbox" checked={on} onChange={() => toggleOne(k)} className="mt-0.5 accent-cyan-600" />
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-800 truncate">{v.model || '(no model)'}</div>
                              <div className="text-slate-500 truncate text-[11px]">{[v.engine, v.ecu_type, v.vehicle_type].filter(Boolean).join(' · ') || '—'}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Step 3 — option + image */}
      {vehicles.length > 0 && selected.size > 0 && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
          <div>
            <Label>3. Tuning option</Label>
            <select className={inputCls} value={optionSlug} onChange={(e) => setOptionSlug(e.target.value)} data-testid="instructions-quick-option">
              <option value="">— select —</option>
              {optionSlugs.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
            </select>
            <Label className="mt-3">Optional text override</Label>
            <textarea rows={2} className={inputCls} value={textOverride} onChange={(e) => setTextOverride(e.target.value)} placeholder="Leave blank to keep the price-DB hint" data-testid="instructions-quick-override" />
            <Label className="mt-3">Admin notes</Label>
            <input type="text" className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="instructions-quick-notes" />
          </div>
          <div>
            <Label>4. Upload photo and/or PDF</Label>
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" onChange={onPickImage} className={inputCls} data-testid="instructions-quick-image" />
            {imagePreview && (
              <div className="mt-2 aspect-[16/10] bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                <img src={imagePreview} alt="" className="w-full h-full object-contain" />
              </div>
            )}
            <Label className="mt-3">PDF manual</Label>
            <input
              type="file" accept="application/pdf,.pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              className={inputCls}
              data-testid="instructions-quick-pdf"
            />
            {pdfFile && (
              <div className="mt-1 flex items-center gap-1.5 text-xs text-rose-700">
                <FileText className="w-3.5 h-3.5" />
                <span className="truncate">{pdfFile.name}</span>
                <button onClick={() => setPdfFile(null)} className="text-slate-400 hover:text-rose-600 underline ml-1">remove</button>
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">Customers can open it right on the order page, page by page.</div>
          </div>
        </section>
      )}

      {/* Save bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button
            onClick={onSave}
            disabled={saving || (!image && !pdfFile) || !optionSlug || selected.size === 0}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            data-testid="instructions-quick-save"
          >
            <Check className="w-4 h-4" />
            {saving ? 'Saving…' : `Apply to ${selected.size} ${selected.size === 1 ? 'vehicle' : 'vehicles'}`}
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * EntriesTable — flat list view: 1 row = 1 vehicle target. Each row carries a
 * thumbnail and a "Replace photo" file-picker; replacing the photo for an
 * entry that shares its image with siblings transparently splits it off into
 * its own instruction so other vehicles are not affected.
 */
function EntriesTable({ token, entries, loading, onChanged }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [busyKey, setBusyKey] = useState(null);

  const replacePhoto = async (entry, file) => {
    const key = `${entry.instruction_id}:${entry.row_index}`;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append('row_index', String(entry.row_index));
      fd.append('image', file);
      const r = await fetch(`${API_URL}/api/instructions/${entry.instruction_id}/replace-image-for`, {
        method: 'POST', headers, body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert('Replace failed: ' + (err.detail || r.status));
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (j.action === 'split_into_new') {
        // Reassuring nudge so the admin knows what happened.
        // (Other rows of the original instruction were not affected.)
        // Avoid blocking alert for happy-path; toast-style would be ideal.
        console.info('[instructions] entry split into a new instruction', j.new_instruction?.id);
      }
      onChanged && onChanged();
    } finally { setBusyKey(null); }
  };

  if (loading && entries.length === 0) {
    return <div className="py-10 text-center text-slate-500 text-sm">Loading entries…</div>;
  }
  if (entries.length === 0) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm" data-testid="instructions-entries-empty">
        <FileImage className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        No entries match your filters.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="instructions-entries-table">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
        <b className="text-slate-700">{entries.length}</b> entries · click <b>Replace photo</b> on any row to swap its image —
        if the image is shared with other vehicles, the entry is split off into its own record automatically.
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2 w-20">Photo</th>
              <th className="text-left px-3 py-2">Option</th>
              <th className="text-left px-3 py-2">Vehicle</th>
              <th className="text-left px-3 py-2">Brand</th>
              <th className="text-left px-3 py-2">Model</th>
              <th className="text-left px-3 py-2">Engine</th>
              <th className="text-left px-3 py-2">ECU</th>
              <th className="text-right px-3 py-2">Shared</th>
              <th className="text-right px-3 py-2 w-44" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const key = `${e.instruction_id}:${e.row_index}`;
              const busy = busyKey === key;
              return (
                <tr key={key} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`instructions-entry-${key}`}>
                  <td className="px-3 py-2">
                    <div className="w-14 h-10 bg-slate-100 rounded overflow-hidden border border-slate-200">
                      <Thumb url={e.image_url} />
                    </div>
                  </td>
                  <td className="px-3 py-2"><code className="text-[11px] px-1 bg-slate-100 rounded">{e.option_slug}</code></td>
                  <td className="px-3 py-2 text-slate-600">{e.vehicle_type || <span className="text-slate-300">any</span>}</td>
                  <td className="px-3 py-2 font-medium">{e.manufacturer || <span className="text-slate-300">any</span>}</td>
                  <td className="px-3 py-2">{e.model || <span className="text-slate-300">any</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{e.engine || <span className="text-slate-300">any</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{e.ecu_type || <span className="text-slate-300">any</span>}</td>
                  <td className="px-3 py-2 text-right">
                    {e.shares_image_with > 0 ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-bold" title={`This image is shared with ${e.shares_image_with} other vehicles. Replacing it will split this entry off automatically.`}>
                        +{e.shares_image_with}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-[10px]">solo</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <label
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold cursor-pointer ${busy ? 'bg-slate-200 text-slate-500' : 'bg-cyan-500 text-white hover:bg-cyan-600'}`}
                      data-testid={`instructions-entry-replace-${key}`}
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      {busy ? 'Replacing…' : 'Replace photo'}
                      <input
                        type="file" accept=".png,.jpg,.jpeg,.webp,.gif"
                        className="hidden"
                        disabled={busy}
                        onChange={(ev) => {
                          const f = ev.target.files?.[0];
                          ev.target.value = ''; // allow re-selecting the same filename
                          if (f) replacePhoto(e, f);
                        }}
                      />
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-slate-100">
        {entries.map((e) => {
          const key = `${e.instruction_id}:${e.row_index}`;
          const busy = busyKey === key;
          return (
            <div key={`m-${key}`} className="p-3 flex gap-3" data-testid={`instructions-entry-card-${key}`}>
              <div className="w-20 h-14 bg-slate-100 rounded overflow-hidden border border-slate-200 flex-shrink-0">
                <Thumb url={e.image_url} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                  <code className="text-[10px] px-1 bg-slate-100 rounded">{e.option_slug}</code>
                  {e.shares_image_with > 0 ? (
                    <span className="text-[9px] px-1 py-0.5 bg-amber-100 text-amber-800 rounded font-bold">+{e.shares_image_with} shared</span>
                  ) : (
                    <span className="text-[9px] text-slate-400">solo</span>
                  )}
                </div>
                <div className="text-sm font-medium text-slate-800 truncate">
                  {e.manufacturer || 'any'} {e.model || ''}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[e.vehicle_type, e.engine, e.ecu_type].filter(Boolean).join(' · ') || '—'}
                </div>
                <label
                  className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold cursor-pointer ${busy ? 'bg-slate-200 text-slate-500' : 'bg-cyan-500 text-white hover:bg-cyan-600'}`}
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  {busy ? 'Replacing…' : 'Replace photo'}
                  <input
                    type="file" accept=".png,.jpg,.jpeg,.webp,.gif"
                    className="hidden"
                    disabled={busy}
                    onChange={(ev) => {
                      const f = ev.target.files?.[0];
                      ev.target.value = '';
                      if (f) replacePhoto(e, f);
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/** Slide-in drawer attached to the form modal — lists every vehicle in the
 *  price DB that uses the source row's ECU. Admin checks the boxes for the
 *  vehicles they want to add as new targeting rows.
 *
 *  - Sticky header with ECU label + close button.
 *  - Live search across brand/model/engine.
 *  - Grouped by brand for fast scanning.
 *  - "Select all visible" / "Deselect all" toggle inside the search bar.
 *  - Sticky footer with the Apply button + count of selections.
 */
function SimilarPanel({ ecu, search, onSearch, grouped, count, total, loading, selected, toggle, allSelected, toggleAll, onApply, onClose }) {
  return (
    <div
      className="absolute inset-y-0 right-0 w-full sm:w-[380px] bg-white border-l border-slate-200 shadow-2xl z-10 flex flex-col rounded-r-2xl"
      data-testid="instructions-similar-panel"
    >
      <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
              <Search className="w-4 h-4 text-cyan-600" /> Find similar
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              ECU: <code className="px-1 rounded bg-white border border-slate-200">{ecu}</code>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-800" data-testid="instructions-similar-close"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-slate-100 space-y-2">
        <input
          type="text" placeholder="Search brand, model, engine…"
          value={search} onChange={(e) => onSearch(e.target.value)}
          className={inputCls}
          data-testid="instructions-similar-search"
        />
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span><b>{count}</b> of {total} vehicles · <b className="text-cyan-700">{selected.size}</b> selected</span>
          <button
            onClick={toggleAll}
            disabled={count === 0}
            className="px-2 py-0.5 text-cyan-600 hover:bg-cyan-50 rounded disabled:opacity-50 font-bold"
            data-testid="instructions-similar-select-all"
          >
            {allSelected ? 'Deselect all' : 'Select all visible'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Searching price DB…</div>
        ) : count === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="instructions-similar-empty">
            {total === 0 ? `No other vehicles in the price DB use ${ecu}.` : 'No matches for that search.'}
          </div>
        ) : (
          grouped.map(([brand, items]) => (
            <div key={brand} className="mb-3" data-testid={`instructions-similar-group-${brand}`}>
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 sticky top-0 bg-white z-1">{brand} ({items.length})</div>
              {items.map((v) => {
                const k = similarKey(v);
                const isOn = selected.has(k);
                return (
                  <label
                    key={k}
                    className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${isOn ? 'bg-cyan-50' : 'hover:bg-slate-50'}`}
                    data-testid={`instructions-similar-row-${k}`}
                  >
                    <input
                      type="checkbox" checked={isOn} onChange={() => toggle(k)}
                      className="mt-0.5 accent-cyan-600"
                      data-testid={`instructions-similar-cb-${k}`}
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{v.model || '(no model)'}</div>
                      <div className="text-slate-500 truncate">
                        {[v.engine, v.vehicle_type].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
        <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">Cancel</button>
        <button
          onClick={onApply}
          disabled={selected.size === 0}
          className="px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
          data-testid="instructions-similar-apply"
        >
          <Check className="w-4 h-4" /> Add {selected.size > 0 ? selected.size : ''} as rows
        </button>
      </div>
    </div>
  );
}



/**
 * ReconcileSlugModal — re-map a single orphan slug onto a current
 * tuning-option key. Drives POST /api/instructions/rename-slug.
 *
 * The orphan banner (parent) lists every slug present in
 * `option_instructions.option_slug` that no longer exists in
 * `tuning_options.option_key`. Admin picks a live slug, confirms, and
 * every affected instruction is updated in one shot. The new slug is
 * highlighted as "in catalogue" so the admin can spot typos before
 * applying.
 */
function ReconcileSlugModal({ token, orphan, optionSlugs, onClose, onDone }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [newSlug, setNewSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const filteredOptions = useMemo(() => {
    const s = filter.trim().toLowerCase();
    if (!s) return optionSlugs;
    return optionSlugs.filter((o) =>
      o.key.toLowerCase().includes(s) || (o.name || '').toLowerCase().includes(s)
    );
  }, [optionSlugs, filter]);

  const onSubmit = async () => {
    if (!newSlug) { alert('Pick a target slug first.'); return; }
    if (!window.confirm(
      `Re-map ${orphan.instruction_count} instruction(s) from "${orphan.old_slug}" → "${newSlug}"?\n\nThis cannot be undone automatically.`
    )) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/instructions/rename-slug`, {
        method: 'POST', headers,
        body: JSON.stringify({ old_slug: orphan.old_slug, new_slug: newSlug }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert('Re-map failed: ' + (j.detail || r.status));
        return;
      }
      alert(`Re-mapped ${j.updated} instruction(s) from "${orphan.old_slug}" to "${newSlug}".`);
      onDone();
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="instructions-reconcile-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-slate-800">Reconcile orphaned slug</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-500 hover:text-slate-800"
            data-testid="instructions-reconcile-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <div className="text-slate-600">Old slug (no longer in catalogue):</div>
            <div className="mt-1 flex items-center gap-2">
              <code className="font-mono text-amber-900 font-bold text-base bg-white px-2 py-0.5 rounded border border-amber-300">
                {orphan.old_slug}
              </code>
              <span className="text-xs text-amber-700">
                Used by <b>{orphan.instruction_count}</b> instruction{orphan.instruction_count === 1 ? '' : 's'}
              </span>
            </div>
          </div>

          <div>
            <Label>Re-map to current tuning option *</Label>
            <input
              type="text"
              placeholder="Filter options…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={`${inputCls} mb-2`}
              data-testid="instructions-reconcile-filter"
            />
            <div
              className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100"
              data-testid="instructions-reconcile-list"
            >
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-slate-500">
                  No tuning options match your filter.
                </div>
              ) : filteredOptions.map((o) => (
                <label
                  key={o.key}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-slate-50 ${newSlug === o.key ? 'bg-cyan-50' : ''}`}
                  data-testid={`instructions-reconcile-option-${o.key}`}
                >
                  <input
                    type="radio"
                    name="new-slug"
                    checked={newSlug === o.key}
                    onChange={() => setNewSlug(o.key)}
                    className="accent-cyan-600"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 truncate">{o.name}</div>
                    <code className="text-[11px] text-slate-500 font-mono">{o.key}</code>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Only published tuning options from the live catalogue are listed.
            </p>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="instructions-reconcile-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={saving || !newSlug}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            data-testid="instructions-reconcile-apply"
          >
            <Check className="w-4 h-4" />
            {saving ? 'Re-mapping…' : `Re-map ${orphan.instruction_count} → "${newSlug || '…'}"`}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * TestMatchModal — dry-run the /instructions/match logic against a single
 * (vehicle_type, brand, model, engine, ecu_type) tuple picked from the
 * live price catalog. Renders a field-by-field verdict so admins can
 * spot exactly which target array is blocking a match after a routine
 * Excel refresh (e.g. "Ducato" → "Ducato 2016-2024"). Backed by
 *   GET /api/instructions/{id}/test-match
 * which reuses the same fuzzy-match helpers the customer endpoint uses.
 */
function TestMatchModal({ token, instruction, onClose }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [vehicleType, setVehicleType] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [engine, setEngine] = useState('');
  const [ecuType, setEcuType] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const cascade = useCascade(token, vehicleType, manufacturer, model);

  // Load engines whenever (vehicle_type, brand, model) are all set — the
  // cascade hook stops at ECUs so we fetch engines directly.
  const [engines, setEngines] = useState([]);
  useEffect(() => {
    if (!vehicleType || !manufacturer || !model) { setEngines([]); return; }
    (async () => {
      const p = new URLSearchParams({ vehicle_type: vehicleType, brand: manufacturer, model });
      const r = await fetch(`${API_URL}/api/price-catalog/engines?${p.toString()}`, { headers });
      const j = r.ok ? await r.json() : null;
      setEngines(Array.isArray(j) ? j : (j?.engines || []));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, manufacturer, model, token]);

  const runTest = async () => {
    if (!instruction) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (vehicleType)  p.append('vehicle_type', vehicleType);
      if (manufacturer) p.append('manufacturer', manufacturer);
      if (model)        p.append('model', model);
      if (engine)       p.append('engine', engine);
      if (ecuType)      p.append('ecu_type', ecuType);
      const r = await fetch(`${API_URL}/api/instructions/${instruction.id}/test-match?${p.toString()}`, { headers });
      const j = r.ok ? await r.json() : { matches: false, overall_reason: `HTTP ${r.status}`, breakdown: [] };
      setResult(j);
    } catch (e) {
      setResult({ matches: false, overall_reason: `Network error: ${e.message}`, breakdown: [] });
    } finally {
      setLoading(false);
    }
  };

  if (!instruction) return null;

  const verdictColor = (v) => ({
    match:    'bg-emerald-50 border-emerald-200 text-emerald-700',
    miss:     'bg-rose-50 border-rose-200 text-rose-700',
    wildcard: 'bg-slate-50 border-slate-200 text-slate-500',
    no_query: 'bg-amber-50 border-amber-200 text-amber-700',
  }[v] || 'bg-slate-50 border-slate-200 text-slate-500');

  const verdictLabel = (v) => ({
    match: 'MATCH', miss: 'MISS', wildcard: 'ANY', no_query: 'SKIPPED',
  }[v] || v);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="test-match-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Beaker className="w-5 h-5 text-emerald-600" /> Test Match
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Instruction <span className="font-mono">{instruction.id.slice(0, 8)}</span> · option <b>{instruction.option_slug}</b>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700"
            data-testid="test-match-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-600">Vehicle type</span>
              <select
                className={inputCls} value={vehicleType}
                onChange={(e) => { setVehicleType(e.target.value); setManufacturer(''); setModel(''); setEngine(''); setEcuType(''); setResult(null); }}
                data-testid="test-match-vehicle-type"
              >
                <option value="">— pick —</option>
                {cascade.vehicleTypes.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-600">Manufacturer</span>
              <select
                className={inputCls} value={manufacturer}
                onChange={(e) => { setManufacturer(e.target.value); setModel(''); setEngine(''); setEcuType(''); setResult(null); }}
                disabled={!vehicleType}
                data-testid="test-match-manufacturer"
              >
                <option value="">— pick —</option>
                {cascade.brands.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-600">Model</span>
              <select
                className={inputCls} value={model}
                onChange={(e) => { setModel(e.target.value); setEngine(''); setEcuType(''); setResult(null); }}
                disabled={!manufacturer}
                data-testid="test-match-model"
              >
                <option value="">— pick —</option>
                {cascade.models.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-600">Engine</span>
              <select
                className={inputCls} value={engine}
                onChange={(e) => { setEngine(e.target.value); setResult(null); }}
                disabled={!model}
                data-testid="test-match-engine"
              >
                <option value="">— pick —</option>
                {engines.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-xs font-semibold text-slate-600">ECU type</span>
              <select
                className={inputCls} value={ecuType}
                onChange={(e) => { setEcuType(e.target.value); setResult(null); }}
                disabled={!model}
                data-testid="test-match-ecu"
              >
                <option value="">— pick —</option>
                {cascade.ecus.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={runTest}
              disabled={loading}
              className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-bold rounded-lg flex items-center gap-2 disabled:opacity-50"
              data-testid="test-match-run-btn"
            >
              <Beaker className="w-4 h-4" />
              {loading ? 'Testing…' : 'Run test'}
            </button>
            <p className="text-xs text-slate-500 self-center">
              Leave any field empty to simulate a wildcard on the customer side.
            </p>
          </div>

          {result && (
            <div className="space-y-3" data-testid="test-match-result">
              <div className={`px-4 py-3 rounded-lg border ${result.matches ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'}`}>
                <div className="flex items-center gap-2 font-bold text-sm">
                  {result.matches
                    ? <Check className="w-5 h-5 text-emerald-600" />
                    : <AlertTriangle className="w-5 h-5 text-rose-600" />}
                  <span className={result.matches ? 'text-emerald-800' : 'text-rose-800'}>
                    {result.matches ? 'MATCH — this photo will render' : 'NO MATCH — this photo will NOT render'}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 pl-7">{result.overall_reason}</p>
              </div>

              <div className="space-y-2">
                {result.breakdown.map((b) => (
                  <div key={b.field} className={`px-3 py-2 rounded-lg border text-xs ${verdictColor(b.verdict)}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-bold uppercase tracking-wider">{b.field.replace(/_/g, ' ')}</div>
                      <div className="text-[10px] font-mono">{verdictLabel(b.verdict)}</div>
                    </div>
                    <div className="mt-1 leading-relaxed">{b.reason}</div>
                    {b.matched_via && b.matched_via.length > 0 && (
                      <div className="mt-1 text-[11px] opacity-75">
                        Matched via: {b.matched_via.map((m) => `"${m.stored}" (norm. "${m.stored_norm}")`).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
            data-testid="test-match-done-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
